import { useState } from 'react';

import {
  pollResumeJsoRightAfterUpload,
  fetchParsedResume,
} from '@zr/utilities/api/profile';
import RequestLimitError from '@zr/utilities/api/RequestLimitError';
import { NAME, PHONE_NUMBER, MOBILE } from '@zr/utilities/profileFields';

import {
  STRING_CHECK_IN_FAILED_PARSE_MESSAGE,
  STRING_SCREEN_FAILED_PARSE_MESSAGE,
} from '@zr/jobseeker-public/apps/jobseeker-screens/labels';

// value matching successful upload_parse_status in profile jso
const SUCCESS_PARSE_STATUS = 'success';

/**
 * Custom hook that provides an isLoading boolean and <ResumeUpload /> handlers (start, end, error)
 * It will run the argument callback on any kind of upload resume result (success/fail/slow) with the parsing results
 *
 * @param {function} onResumeContinueWithResult callback called with the parse result
 * @param {boolean} autologin if autologin, then skip parsing because those users cannot read parsed resume data
 * @param {boolean} isCheckIn if part of recruiter check-in, change the parse error message
 * @returns object with isLoading boolean and resume upload handlers
 */
export function useUploadAndParseResume(
  onResumeContinueWithResult,
  autologin = false,
  isCheckIn = false,
) {
  // show when resume is uploading or parsing
  const [isLoading, setIsLoading] = useState(false);

  const handleUploadStart = () => {
    setIsLoading(true);
  };

  /**
   * Runs the callback with the parse result filled in
   *
   * @param {string} option.errorMessage message to display on the next screen if something went wrong
   * @param {string} option.fileName file name job seeker picked, used to show their uploaded file on the next screen
   * @param {string} option.parseResult used in logging to indicate how the parse went
   * @param {string} option.parseContent used in logging to indicate what fields (name, phone_number) were successfully parsed
   * @param {string} option.resume raw resume data if next screen needs to upload the resume again
   * @param {object} option.resumeJso parsed resume data jso
   * @param {boolean} option.uploadedResume true if resume was uploaded
   */
  const runResumeContinueWithResult = ({
    errorMessage = null,
    fileName = '',
    parseResult = '',
    parseContent = 'none',
    resume = '',
    resumeJso = null,
    uploadedResume = false,
  }) => {
    onResumeContinueWithResult({
      errorMessage,
      fileName,
      parseResult,
      parseContent,
      resume,
      resumeJso,
      uploadedResume,
    });
  };

  // this usually happens when there's an upload resume error, but also handles errors before uploading
  // this can also mean a network error or invalid resume error with useResumePredictorValidation
  const handleUploadError = ({ message: errorMessage }, file, resume) => {
    if (file && resume) {
      // this condition happens when we fail to upload
      const { name: fileName } = file;
      runResumeContinueWithResult({
        errorMessage,
        fileName,
        parseResult: 'upload_error',
        // send the resume data to try to resubmit
        resume,
        // default 'uploadedResume' property is false
      });
    } else {
      // probably failed to pick a good file type, etc.
      runResumeContinueWithResult({
        errorMessage,
        parseResult: 'file_pick_error',
        // default 'uploadedResume' property is false
      });
    }
  };

  const handleUploadEnd = async ({ resume: { filename: fileName } }) => {
    // autologin users cannot get profile or resume jso
    if (autologin) {
      runResumeContinueWithResult({
        fileName,
        parseResult: 'autologin_skip',
        uploadedResume: true,
      });
      return;
    }
    try {
      const profileJso = await pollResumeJsoRightAfterUpload();

      if (profileJso?.upload_parse_status !== SUCCESS_PARSE_STATUS) {
        // resume upload succeeded but parsing complained about something
        const errorMessage = isCheckIn ? STRING_CHECK_IN_FAILED_PARSE_MESSAGE : STRING_SCREEN_FAILED_PARSE_MESSAGE;
        throw new Error(errorMessage);
      }

      // if got here, resume jso and its id should be available
      const resumeJsoId = profileJso.resume.resume_jso_id;
      const resumeJso = await fetchParsedResume(resumeJsoId);

      // get how complete of a parse this was for logging
      const parsedName = !!resumeJso[NAME];
      const parsedPhoneNumber = !!(resumeJso[PHONE_NUMBER] || resumeJso[MOBILE]);
      let parseContent = 'none';
      if (parsedName && parsedPhoneNumber) {
        parseContent = 'complete';
      } else if (parsedName) {
        parseContent = 'name_only';
      } else if (parsedPhoneNumber) {
        parseContent = 'phone_number_only';
      }

      // resume upload and parse succeeded, use resumeJso info
      runResumeContinueWithResult({
        fileName,
        parseContent,
        parseResult: 'complete_parse',
        resumeJso,
        uploadedResume: true,
      });
    } catch (e) {
      if (e instanceof RequestLimitError) {
        // resume jso not ready, but nothing is 'wrong' other than we are slow at parsing
        runResumeContinueWithResult({
          fileName,
          parseResult: 'slow_parse',
          uploadedResume: true,
        });
      } else {
        // resume uploaded but something went wrong with getting the resume jso
        runResumeContinueWithResult({
          errorMessage: e.message,
          fileName,
          parseResult: 'parse_error',
          uploadedResume: true,
        });
      }
    }
  };

  return {
    isLoading,
    handleUploadStart,
    handleUploadEnd,
    handleUploadError,
  };
}

export default useUploadAndParseResume;
